import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { Fragment, type JSX } from 'react';
import { useLocale } from '@repo/i18n';
import { CheckoutQuestionWithAnswer } from '@repo/types';
import TextFieldStyleUtils from 'src/components/utils/jss/TextFieldStyleUtils';
import { validateBilberryQuestion } from '@repo/widget-utils/checkout-info-helper';

type Props = {
    showErrors: boolean;
    onChange(question: CheckoutQuestionWithAnswer): void;
    question: CheckoutQuestionWithAnswer;
    disabled?: boolean;
    /** Context prefix for error messages (e.g., "Traveler 1") */
    errorContext?: string;
};

export default function BilberryQuestionInput(props: Props): JSX.Element {
    const { showErrors, onChange, question, disabled, errorContext } = props;
    const isText = question.type === 'text';
    const isTextArea = question.type === 'textarea';
    const isCheckbox = question.type === 'checkbox';
    const theme = useTheme();
    const { t } = useLocale();
    // Use question.key directly - it's already unique per question
    const fieldId = `question-${question.key}`;
    const errorId = `question-${question.key}-error`;

    const showValidationError = !validateBilberryQuestion(question) && showErrors;

    // Build contextual error message: "Traveler 1 First name is required"
    const errorMessage = errorContext
        ? `${errorContext} ${question.title} ${t.is_required}`
        : `${question.title} ${t.is_required}`;

    const updateValue = (latestValue: string | boolean) => {
        const question: CheckoutQuestionWithAnswer = {
            ...props.question,
            answer: latestValue,
        };

        onChange(question);
    };

    return (
        <Fragment>
            {(isText || isTextArea) && (
                <FormControl>
                    <InputLabel
                        htmlFor={fieldId}
                        sx={TextFieldStyleUtils.inputLabelAboveFieldSx}
                        required={question.mandatory}
                        error={showValidationError}
                    >
                        {question.title}
                    </InputLabel>
                    <TextField
                        id={fieldId}
                        required={question.mandatory}
                        disabled={disabled}
                        error={showValidationError}
                        aria-invalid={showValidationError || undefined}
                        aria-describedby={showValidationError ? errorId : undefined}
                        value={question.answer}
                        onChange={(e) => updateValue(e.currentTarget.value)}
                        type="text"
                        variant="outlined"
                        color="secondary"
                        margin="dense"
                        size="small"
                        multiline={isTextArea}
                        minRows={isTextArea ? 3 : undefined}
                        name={question.key}
                        placeholder={question.placeholder ?? undefined}
                        helperText={showValidationError ? errorMessage : undefined}
                        FormHelperTextProps={
                            showValidationError ? { id: errorId, role: 'alert' } : undefined
                        }
                    />
                </FormControl>
            )}

            {isCheckbox && (
                <Box>
                    <FormControlLabel
                        sx={{
                            '& .MuiFormControlLabel-label': {
                                color:
                                    showErrors && !question.answer && question.mandatory
                                        ? theme.palette.error.main
                                        : undefined,
                            },
                        }}
                        control={
                            <Checkbox
                                checked={question.answer as boolean}
                                onChange={(_, checked) => updateValue(checked)}
                                name={question.key}
                                inputProps={{
                                    'aria-invalid': showValidationError || undefined,
                                    'aria-describedby': showValidationError ? errorId : undefined,
                                }}
                            />
                        }
                        labelPlacement="end"
                        label={question.title}
                        color="secondary"
                    />
                    {showErrors && !question.answer && question.mandatory && (
                        <Typography
                            fontSize={12}
                            color="error"
                            mt={-1}
                            ml="14px"
                            role="alert"
                            id={errorId}
                        >
                            {errorContext
                                ? `${errorContext}: ${t.pleaseConfirm.parsed(question.title)}`
                                : t.pleaseConfirm.parsed(question.title)}
                        </Typography>
                    )}
                </Box>
            )}
        </Fragment>
    );
}
